import type { ChangeEvent } from 'react'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import { Dialog } from '@gto/ui/dialog'

import {
  type CryptoRedemptionOption,
  useRedemptionsRedemptionOptionsCryptoRedeemCreate,
} from '@/api/gen'
import { TextInput } from '@/components/text-input'
import { useEdgeBalance } from '@/hooks/use-edge-balance'
import { getAxiosErrorMessage } from '@/utils/helpers'

import { CryptoDescription } from './crypto-description'
import { CryptoRedeemSum } from './crypto-redeem-sum'
import { RedeemOptionChip } from './redeem-option-chip'

export type CryptoRedeemModalProps = {
  open: boolean
  onClose: () => void
  edge: number
  coin: CryptoRedemptionOption
}

export const CryptoRedeemModal = ({ open, onClose, edge, coin }: CryptoRedeemModalProps) => {
  const { formatMessage } = useIntl()
  const { reduceBalance } = useEdgeBalance()

  const [wallet, setWallet] = useState('')
  const [transaction, setTransaction] = useState<{
    status: 'success' | 'warning'
    message?: string
  } | null>(null)

  const { mutate: confirmRedeem, isPending } = useRedemptionsRedemptionOptionsCryptoRedeemCreate({
    mutation: {
      onError: (err: AxiosError) => {
        setTransaction({
          status: 'warning',
          message:
            getAxiosErrorMessage(err) ||
            formatMessage({ id: 'edge.premiumRedeem.confirmCrypto.error' }),
        })
      },
      onSuccess: (data) => {
        setTransaction({
          status: data.success ? 'success' : 'warning',
          message:
            data.message || formatMessage({ id: 'edge.premiumRedeem.confirmCrypto.success' }),
        })
        if (data.success) {
          reduceBalance(edge)
        }
      },
    },
  })
  const onSubmit = () => {
    const isValid = validate(wallet)
    setTransaction(null)

    if (isValid) {
      confirmRedeem({
        data: {
          cryptocurrency_id: coin.id,
          wallet_address: wallet,
          edge_amount: edge,
        },
      })
    }
  }

  const onChangeWallet = (event: ChangeEvent<HTMLInputElement>) => {
    setWallet(event.target.value)
  }

  const validate = (input: string): boolean => {
    return Boolean(
      input.length &&
        input.length >= coin.min_wallet_length &&
        input.length <= coin.max_wallet_length,
    )
  }

  const handleClose = () => {
    onClose()
    setWallet('')
    setTransaction(null)
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          handleClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-full max-w-[500px] sm:top-[60px] sm:translate-y-0">
          <Dialog.Header>
            <FormattedMessage
              id={
                transaction?.status === 'success'
                  ? 'edge.premiumRedeem.confirmCrypto.success.title'
                  : 'edge.premiumRedeem.confirmCrypto.title'
              }
            />
          </Dialog.Header>
          <Dialog.Content>
            <div className="flex flex-col gap-8">
              {transaction?.status === 'success' ? (
                <Alert
                  heading={transaction.message}
                  severity="success"
                />
              ) : (
                <>
                  <div>
                    <CryptoRedeemSum edge={edge} />
                    <RedeemOptionChip
                      icon={coin.icon}
                      name={coin.cryptocurrency}
                    />
                  </div>

                  <div className="flex flex-col gap-4">
                    <TextInput
                      label="common.labels.enterYourWallet"
                      helper={
                        <FormattedMessage
                          id="edge.premiumRedeem.confirmCrypto.walletHelperText"
                          values={{
                            min: coin.min_wallet_length,
                            max: coin.max_wallet_length,
                          }}
                        />
                      }
                      input={{
                        size: 'medium',
                        value: wallet,
                        disabled: isPending,
                        onChange: onChangeWallet,
                      }}
                    />
                    {transaction?.status === 'warning' && (
                      <Alert
                        heading={transaction.message}
                        severity="warning"
                      />
                    )}
                  </div>
                  <CryptoDescription />
                </>
              )}
            </div>
          </Dialog.Content>
          <Dialog.Footer>
            <Button
              color="secondary"
              disabled={isPending}
              size="large"
              variant="contained"
              onClick={handleClose}
            >
              <FormattedMessage
                id={
                  transaction?.status === 'success'
                    ? 'common.controls.close'
                    : 'common.controls.cancel'
                }
              />
            </Button>
            {transaction?.status !== 'success' && (
              <Button
                color="primary"
                disabled={!validate(wallet) || edge <= 0 || isPending}
                loading={isPending}
                size="large"
                variant="contained"
                onClick={onSubmit}
              >
                <FormattedMessage id="common.controls.completeRedemption" />
              </Button>
            )}
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
